import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Lock, Loader2, Eye, EyeOff } from "lucide-react";
import { useAuth, formatApiError } from "@/context/AuthContext";
import { Seo } from "@/components/Seo";

const field = "w-full bg-[color:var(--charcoal)] border border-[color:var(--line)] rounded-xl px-4 py-3.5 text-white placeholder:text-[color:var(--chrome-3)] focus:outline-none focus:border-[color:var(--cyan)] transition-colors";

export default function Join() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ username: "", email: "", password: "", collector_handle: "", is_anonymous: false });
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const up = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.username.trim() || !form.email.trim() || form.password.length < 8) {
      setError("Please enter a username, a valid email, and a password of at least 8 characters.");
      return;
    }
    setBusy(true);
    try {
      await register({ ...form, email: form.email.trim().toLowerCase(), username: form.username.trim() });
      navigate("/welcome", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-black min-h-screen flex items-center justify-center px-5 py-20">
      <Seo title="Open Your Free Account — CardScience HQ" description="Create your free CardScience HQ collector account." path="/join" noindex />
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
        className="w-full max-w-md rounded-3xl holo-border holo-glow p-8 bg-black/60 backdrop-blur-xl">
        <p className="font-tech text-[11px] tracking-[0.4em] text-[color:var(--chrome-3)] text-center">CARDSCIENCE<span className="text-holo">HQ</span></p>
        <h1 className="h-display text-3xl text-white text-center mt-4">OPEN YOUR FREE ACCOUNT</h1>
        <p className="text-sm text-[color:var(--chrome-3)] text-center mt-2">A private vault for your collection.</p>

        <form onSubmit={submit} className="mt-7 space-y-3">
          <input data-testid="join-username" value={form.username} onChange={(e) => up("username", e.target.value)} placeholder="Username" className={field} />
          <input data-testid="join-email" type="email" value={form.email} onChange={(e) => up("email", e.target.value)} placeholder="Email" className={field} />
          <div className="relative">
            <input data-testid="join-password" type={show ? "text" : "password"} value={form.password} onChange={(e) => up("password", e.target.value)} placeholder="Password (min 8 characters)" className={field} />
            <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[color:var(--chrome-3)] hover:text-white">
              {show ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          <input data-testid="join-handle" value={form.collector_handle} onChange={(e) => up("collector_handle", e.target.value)} placeholder="Collector handle (optional)" className={field} />
          <label className="flex items-center gap-3 cursor-pointer px-1 pt-1">
            <input type="checkbox" data-testid="join-anonymous" checked={form.is_anonymous} onChange={(e) => up("is_anonymous", e.target.checked)} className="accent-[color:var(--cyan)] h-4 w-4" />
            <span className="text-xs text-[color:var(--chrome-2)]">Keep me publicly anonymous</span>
          </label>

          {error && <p data-testid="join-error" className="text-sm text-[color:var(--magenta)]">{error}</p>}

          <button type="submit" data-testid="join-submit" disabled={busy}
            className="btn-holo w-full rounded-xl py-4 text-sm inline-flex items-center justify-center gap-2 disabled:opacity-50">
            {busy ? <><Loader2 size={16} className="animate-spin" /> CREATING…</> : <>CREATE MY VAULT <ArrowRight size={16} /></>}
          </button>
        </form>

        <p className="text-xs text-[color:var(--chrome-3)] text-center mt-5 inline-flex items-center justify-center gap-2 w-full">
          <Lock size={12} className="text-[color:var(--cyan)]" /> Private by default. Your collection stays yours.
        </p>
        <p className="text-sm text-[color:var(--chrome-3)] text-center mt-4">
          Already have an account? <Link to="/signin" className="text-[color:var(--cyan)] hover:underline">Sign In</Link>
        </p>
      </motion.div>
    </div>
  );
}
